import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { goodsIntakeApi } from '../api/goods-intake-api';
import { ConflictError, ForbiddenError, NotFoundError } from '../api/errors';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import {
  acceptedQuantity,
  discrepancyKindLabels,
  isOpenReceipt,
  lineDiscrepancies,
  normalizeReceiptCode,
  postBlocker,
  receiptSourceLabels,
  validateCount,
  type ExpectedReceipt,
  type ReceiptCount,
} from '../domain/goods-intake';
import { intakeErrorMessage, isUnknownOutcome } from '../features/goods-intake/error-message';
import { CancelReceiptDialog, PostReceiptDialog } from '../features/goods-intake/ReceiptDialogs';
import { stockItemMatchesCode } from '../domain/stock';
import { ReceiptStateBadge } from '../features/goods-intake/ReceiptStateBadge';
import { ScanField, type ScanOutcome } from '../features/goods-intake/ScanField';
import { INTAKE_QUERY_KEYS } from '../features/goods-intake/use-intake-queries';
import { STOCK_QUERY_KEYS, useStockReferences } from '../features/stock/use-stock-references';

/** One line as the operator is editing it; quantities stay text until saved. */
interface Draft {
  key: string;
  lineId?: string | undefined;
  stockItemId: string;
  expected: number;
  received: string;
  damaged: string;
  locationId: string;
  note: string;
}

function draftsFrom(receipt: ExpectedReceipt): Draft[] {
  return receipt.lines.map((line) => ({
    key: line.id,
    lineId: line.id,
    stockItemId: line.stockItemId,
    expected: line.expectedQuantity,
    received: line.receivedQuantity === undefined ? '' : String(line.receivedQuantity),
    damaged: line.damagedQuantity ? String(line.damagedQuantity) : '',
    locationId: line.locationId ?? '',
    note: line.note ?? '',
  }));
}

function isDirty(draft: Draft, receipt: ExpectedReceipt): boolean {
  const line = receipt.lines.find((l) => l.id === draft.lineId);
  if (!line) return true;
  return (
    draft.received !== (line.receivedQuantity === undefined ? '' : String(line.receivedQuantity)) ||
    (Number(draft.damaged) || 0) !== (line.damagedQuantity ?? 0) ||
    draft.locationId !== (line.locationId ?? '') ||
    draft.note !== (line.note ?? '')
  );
}

const quantityOf = (text: string): number => (text.trim() === '' ? 0 : Number(text));

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** Counts one receipt and puts it into stock (tidaltech/koi#8101). */
export function GoodsReceiptPage() {
  const { receiptId = '' } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const references = useStockReferences();
  const notice = (location.state as { notice?: string } | null)?.notice;
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [putAwayAt, setPutAwayAt] = useState('');
  const [quantity, setQuantity] = useState('');
  const [dialog, setDialog] = useState<'post' | 'cancel' | null>(null);
  const [saveError, setSaveError] = useState<{ text: string; conflict: boolean } | null>(null);
  const [posted, setPosted] = useState<{ replayed: boolean } | null>(null);

  const receipt = useQuery({
    queryKey: INTAKE_QUERY_KEYS.receipt(receiptId),
    queryFn: () => goodsIntakeApi.getReceipt(receiptId),
    refetchOnWindowFocus: false,
  });
  const current = receipt.data;

  // Start from the saved counts whenever a new version arrives.
  useEffect(() => {
    if (current) setDrafts(draftsFrom(current));
  }, [current?.id, current?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const siteLocations = useMemo(
    () =>
      references.locations
        .filter((l) => l.active && l.siteId === current?.siteId)
        .map((l) => ({ value: l.id, label: references.locationName(l.id), aliases: [l.code] })),
    [references, current?.siteId],
  );

  function show(updated: ExpectedReceipt): void {
    queryClient.setQueryData(INTAKE_QUERY_KEYS.receipt(receiptId), updated);
    void queryClient.invalidateQueries({ queryKey: INTAKE_QUERY_KEYS.receipts });
  }

  function reload(): void {
    setDialog(null);
    setSaveError(null);
    void receipt.refetch();
  }

  const save = useMutation({
    mutationFn: ({ receipt: r, counts }: { receipt: ExpectedReceipt; counts: ReceiptCount[] }) =>
      goodsIntakeApi.recordIntake(r.id, r.version, counts),
    onSuccess: (updated) => {
      setSaveError(null);
      show(updated);
    },
    onError: (failure: unknown) =>
      setSaveError({
        text: isUnknownOutcome(failure)
          ? 'The server didn’t answer, so the counts may not be saved. Save again; it replaces the counts, never adds to them.'
          : intakeErrorMessage(failure, 'The counts could not be saved.'),
        conflict: failure instanceof ConflictError,
      }),
  });

  if (receipt.isLoading || references.loading) return <PageLoadingState label="Loading receipt…" />;
  if (receipt.error instanceof ForbiddenError) {
    return (
      <div className="empty-state">
        <strong>Staff access required</strong>
        <p>
          Sign in with a <span className="mono">@tidalx.ai</span> account to receive goods.
        </p>
      </div>
    );
  }
  if (receipt.error instanceof NotFoundError) {
    return (
      <div className="empty-state">
        <strong>Receipt not found</strong>
        <Link to="/goods-intake">Back to goods intake</Link>
      </div>
    );
  }
  if (!current || receipt.isError) {
    return (
      <ErrorState
        title="The receipt could not be loaded"
        rpcName="GetExpectedReceipt"
        onRetry={() => void receipt.refetch()}
      />
    );
  }

  const open = isOpenReceipt(current);
  const dirty = drafts.filter((draft) => isDirty(draft, current));
  const title = `${current.supplier ?? 'Unknown supplier'}${current.supplierReference ? ` · ${current.supplierReference}` : ''}`;

  function update(key: string, patch: Partial<Draft>): void {
    setDrafts((all) => all.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));
  }

  /** The counts to send, or an error to show. Uncounted lines stay uncounted. */
  function countsToSave(target: ExpectedReceipt): ReceiptCount[] | string {
    const counts: ReceiptCount[] = [];
    for (const draft of drafts.filter((d) => isDirty(d, target))) {
      const name = references.itemName(draft.stockItemId);
      const wasCounted = target.lines.some(
        (l) => l.id === draft.lineId && l.receivedQuantity !== undefined,
      );
      if (draft.received.trim() === '') {
        if (wasCounted) return `${name}: enter 0 if nothing arrived.`;
        if (draft.damaged.trim() || draft.note.trim()) return `${name}: enter how many arrived.`;
        continue;
      }
      const count: ReceiptCount = {
        lineId: draft.lineId,
        stockItemId: draft.lineId ? undefined : draft.stockItemId,
        receivedQuantity: quantityOf(draft.received),
        damagedQuantity: quantityOf(draft.damaged),
        locationId: draft.locationId || undefined,
        note: draft.note,
      };
      const problem = validateCount(count);
      if (problem) return `${name}: ${problem}`;
      counts.push(count);
    }
    return counts;
  }

  async function saveCounts(): Promise<ExpectedReceipt | null> {
    if (!current) return null;
    const counts = countsToSave(current);
    if (typeof counts === 'string') {
      setSaveError({ text: counts, conflict: false });
      return null;
    }
    if (counts.length === 0) return current;
    try {
      return await save.mutateAsync({ receipt: current, counts });
    } catch {
      return null;
    }
  }

  async function review(): Promise<void> {
    const saved = await saveCounts();
    if (!saved) return;
    const blocker = postBlocker(saved);
    if (blocker) {
      setSaveError({ text: blocker, conflict: false });
      return;
    }
    setDialog('post');
  }

  function scan(code: string): ScanOutcome {
    const item = references.items.find(
      (candidate) =>
        normalizeReceiptCode(candidate.sku) === normalizeReceiptCode(code) ||
        stockItemMatchesCode(candidate, code),
    );
    if (!item) return { tone: 'rejected', text: `${code} is not a stock item SKU or barcode.` };
    if (!item.active) return { tone: 'rejected', text: `${item.displayName} is deactivated.` };
    const qty = quantity.trim() === '' ? 1 : Number(quantity);
    if (!Number.isInteger(qty) || qty <= 0) {
      return { tone: 'rejected', text: 'The quantity must be a whole number above 0.' };
    }
    setQuantity('');
    const existing = drafts.find((draft) => draft.stockItemId === item.id);
    const unit = references.unit(item.id);
    if (existing) {
      const received = quantityOf(existing.received) + qty;
      update(existing.key, {
        received: String(received),
        locationId: existing.locationId || putAwayAt,
      });
      return {
        tone: 'accepted',
        text: `Counted ${qty} ${unit} ${item.displayName}: ${received} of ${existing.expected}.`,
      };
    }
    setDrafts((all) => [
      ...all,
      {
        key: `new:${item.id}`,
        stockItemId: item.id,
        expected: 0,
        received: String(qty),
        damaged: '',
        locationId: putAwayAt,
        note: '',
      },
    ]);
    return {
      tone: 'accepted',
      text: `${item.displayName} isn’t on the manifest. Added ${qty} ${unit} as extra.`,
    };
  }

  return (
    <>
      <PageHeader
        title={title}
        description={`${references.siteName(current.siteId)} · ${receiptSourceLabels[current.source]}`}
        backLink={<Link to="/goods-intake">← Goods intake</Link>}
        actions={
          open ? (
            <button type="button" className="button-danger" onClick={() => setDialog('cancel')}>
              Cancel receipt
            </button>
          ) : undefined
        }
      />
      <div className="order-summary">
        <ReceiptStateBadge state={current.state} />
        {current.expectedAt ? (
          <span>Expected {dateTime.format(new Date(current.expectedAt))}</span>
        ) : null}
        {current.identifiers.length > 0 ? (
          <span className="mono">{current.identifiers.join(', ')}</span>
        ) : null}
        {current.notes ? <p className="order-notes">{current.notes}</p> : null}
      </div>
      {notice ? (
        <p className="warning-callout" role="status">
          {notice}
        </p>
      ) : null}
      {posted ? (
        <p className="info-callout" role="status">
          {posted.replayed
            ? 'These goods were already received; nothing changed.'
            : 'Received into stock.'}
        </p>
      ) : null}
      {current.state === 'CANCELLED' ? (
        <p className="warning-callout">
          Cancelled{current.cancelReason ? `: ${current.cancelReason}` : ''}. Nothing was put into
          stock.
        </p>
      ) : null}

      {open ? (
        <section className="scan-panel" aria-labelledby="intake-count-heading">
          <h2 id="intake-count-heading">Count</h2>
          <ScanField
            id="intake-count"
            label="Stock item SKU"
            hint="Each scan counts 1, or the quantity you enter. Counts are kept when you save."
            autoFocus
            onScan={scan}
          >
            <label className="quantity-field">
              <span>Quantity</span>
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                placeholder="1"
                aria-label="Quantity per scan"
              />
            </label>
          </ScanField>
          <div className="intake-put-away">
            <label>
              <span>Put away at</span>
              <SearchableSelect
                ariaLabel="Put away at"
                value={putAwayAt}
                onChange={setPutAwayAt}
                options={siteLocations}
                placeholder="Location for scanned goods"
              />
            </label>
            <button
              type="button"
              disabled={!putAwayAt}
              onClick={() =>
                setDrafts((all) =>
                  all.map((draft) =>
                    draft.locationId ? draft : { ...draft, locationId: putAwayAt },
                  ),
                )
              }
            >
              Use for every line without a location
            </button>
          </div>
        </section>
      ) : null}

      <section className="intake-lines form-surface" aria-labelledby="intake-lines-heading">
        <h2 id="intake-lines-heading">Lines</h2>
        <ul className="intake-line-list">
          {drafts.map((draft) => {
            const name = references.itemName(draft.stockItemId);
            const unit = references.unit(draft.stockItemId);
            const line = current.lines.find((l) => l.id === draft.lineId);
            const counted = draft.received.trim() !== '';
            const preview = counted
              ? lineDiscrepancies({
                  expectedQuantity: draft.expected,
                  receivedQuantity: quantityOf(draft.received),
                  damagedQuantity: quantityOf(draft.damaged),
                })
              : [];
            return (
              <li
                key={draft.key}
                className={`intake-line${isDirty(draft, current) && open ? ' is-dirty' : ''}`}
                aria-label={name}
              >
                <div className="intake-line-heading">
                  <strong>{name}</strong>
                  <span>
                    {draft.expected > 0
                      ? `Expected ${draft.expected} ${unit}`
                      : 'Not on the manifest'}
                  </span>
                  <span className="intake-line-status">
                    {!counted ? (
                      <span className="state-badge">Not counted</span>
                    ) : preview.length === 0 ? (
                      <span className="state-badge state-badge-ready">Matches</span>
                    ) : (
                      preview.map((d) => (
                        <span key={d.kind} className="attention-badge">
                          {discrepancyKindLabels[d.kind]} {d.quantity}
                        </span>
                      ))
                    )}
                  </span>
                </div>
                {open ? (
                  <div className="intake-line-fields">
                    <label className="quantity-field">
                      <span>Received</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        value={draft.received}
                        onChange={(event) => update(draft.key, { received: event.target.value })}
                        aria-label={`Received ${name}`}
                      />
                    </label>
                    <label className="quantity-field">
                      <span>Damaged</span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        value={draft.damaged}
                        onChange={(event) => update(draft.key, { damaged: event.target.value })}
                        aria-label={`Damaged ${name}`}
                        placeholder="0"
                      />
                    </label>
                    <label>
                      <span>Location</span>
                      <SearchableSelect
                        ariaLabel={`Location for ${name}`}
                        value={draft.locationId}
                        onChange={(value) => update(draft.key, { locationId: value })}
                        options={siteLocations}
                        placeholder="Choose a location"
                      />
                    </label>
                    <label>
                      <span>Note</span>
                      <input
                        value={draft.note}
                        onChange={(event) => update(draft.key, { note: event.target.value })}
                        aria-label={`Note for ${name}`}
                        placeholder={quantityOf(draft.damaged) > 0 ? 'What is damaged?' : ''}
                      />
                    </label>
                  </div>
                ) : (
                  <p className="order-card-meta">
                    Received {line?.receivedQuantity ?? 0} {unit}
                    {line?.damagedQuantity ? `, ${line.damagedQuantity} damaged` : ''}
                    {line && acceptedQuantity(line) > 0 ? (
                      <>
                        {' · '}
                        {acceptedQuantity(line)} {unit} to{' '}
                        {references.locationName(line.locationId)}
                        {' · '}
                        <Link to={`/stock/items/${encodeURIComponent(line.stockItemId)}`}>
                          See stock
                        </Link>
                      </>
                    ) : null}
                    {line?.note ? ` · ${line.note}` : ''}
                  </p>
                )}
              </li>
            );
          })}
        </ul>

        {current.state === 'POSTED' ? (
          <div className="intake-posted">
            <p>
              Received by {current.postedBy ?? '—'}
              {current.postedAt ? ` on ${dateTime.format(new Date(current.postedAt))}` : ''}.
            </p>
            {current.discrepancies.length > 0 ? (
              <div className="warning-callout">
                <strong>Discrepancies</strong>
                <ul>
                  {current.discrepancies.map((d) => (
                    <li key={`${d.lineId ?? d.stockItemId}-${d.kind}`}>
                      {discrepancyKindLabels[d.kind]}: {d.quantity} {references.unit(d.stockItemId)}{' '}
                      {references.itemName(d.stockItemId)}
                      {d.note ? ` (${d.note})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="field-hint">Everything matched the manifest.</p>
            )}
          </div>
        ) : null}

        {saveError ? (
          <div role="alert" className="form-error">
            {saveError.text}
            {saveError.conflict ? (
              <button type="button" className="link-button" onClick={reload}>
                Reload receipt
              </button>
            ) : null}
          </div>
        ) : null}
        {open ? (
          <div className="drawer-actions intake-actions">
            {dirty.length > 0 ? (
              <span className="field-hint" role="status">
                {dirty.length === 1 ? '1 line has' : `${dirty.length} lines have`} unsaved counts.
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => void saveCounts()}
              disabled={dirty.length === 0 || save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save counts'}
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => void review()}
              disabled={save.isPending}
            >
              Review and receive
            </button>
          </div>
        ) : null}
      </section>

      {dialog === 'post' ? (
        <PostReceiptDialog
          receipt={current}
          references={references}
          onClose={() => setDialog(null)}
          onReload={reload}
          onPosted={(result) => {
            setDialog(null);
            setPosted({ replayed: result.replayed });
            show(result.receipt);
            void queryClient.invalidateQueries({ queryKey: ['stock'] });
            void queryClient.invalidateQueries({ queryKey: STOCK_QUERY_KEYS.balances });
          }}
        />
      ) : null}
      {dialog === 'cancel' ? (
        <CancelReceiptDialog
          receipt={current}
          onClose={() => setDialog(null)}
          onReload={reload}
          onDone={(updated) => {
            setDialog(null);
            show(updated);
          }}
        />
      ) : null}
    </>
  );
}
